import React, { useState, useEffect } from 'react';
import * as Yup from 'yup';
import {
   Grid,
   GridItem,
   useColorModeValue,
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalCloseButton,
   ModalBody,
   ModalFooter,
   Button,
   useToast,
} from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { renderFormControl, renderSelectFormControl } from 'utils/DynamicFormData';
import { getCities, addCity, updateCity } from '../../../../../redux/actions/city';
// import { getCountries } from '../../../../../redux/actions/country';
// import { getStates } from '../../../../../redux/actions/state';
import '../../../companyProfile/css.css';
import { DynamicInput } from 'components/UIcomponents/Dynamicinput';
import { renderDynamicDropdown } from 'components/UIcomponents/DynamicDropdown';
import { useGlobal } from '../../../../../contexts';

const validationSchema = Yup.object().shape({
   name: Yup.string().required('Name is required').min(1, 'Name must be at least 1 characters long'),
   stId: Yup.string().required('State is required'),
});

const CityMasterForm = ({ isOpen, onClose, isEditMode = false, demoname, currentRow = null, onSave, saveandcontinue = true }) => {
   const [row, setRow] = useState({
      name: '',
      stId: '',
      status: '',
   });

   const [errors, setErrors] = useState({});
   const toast = useToast();
   const textColor = useColorModeValue('#9E9E9E', 'white');
   const dispatch = useDispatch();
   const { stateList } = useGlobal();
   // const stateList = useSelector((state) => state.state.list);

   // useEffect(() => {
   //   if (isOpen) {
   //     dispatch(getCountries());
   //     dispatch(getStates());
   //   }
   // }, [dispatch, isOpen]);

   const clearFormData = () => {
      setRow({
         name: '',
         stId: '',
         status: '',
      });
      setErrors({});
   };

   useEffect(() => {
      if (demoname && row.name === '') {
         setRow((prev) => ({ ...prev, name: demoname }));
      }
      demoname = '';
   }, [demoname, row.name]);

   useEffect(() => {
      if (!isEditMode) {
         clearFormData();
      } else if (isEditMode && currentRow) {
         const selectedState = stateList?.find((state) => state.id === currentRow.stId);

         setRow({
            name: currentRow.name || '',
            stId: currentRow.stId || '',
            status: currentRow.status || '',
         });
      }
   }, [isEditMode, currentRow, stateList]);

   const validateForm = async () => {
      try {
         await validationSchema.validate(row, { abortEarly: false });
         setErrors({});
         return true;
      } catch (err) {
         if (err instanceof Yup.ValidationError) {
            const errorObj = {};
            err.inner.forEach((error) => {
               errorObj[error.path] = error.message;
            });
            setErrors(errorObj);
         }
         return false;
      }
   };

   const handleInputChange = (field, value) => {
      setRow((prev) => {
         const updatedRow = { ...prev, [field]: value };
         return updatedRow;
      });

      setErrors((prevErrors) => {
         const newErrors = { ...prevErrors };
         if (newErrors[field]) {
            delete newErrors[field];
         }
         return newErrors;
      });
   };

   const handleSubmit = async () => {
      const isValid = await validateForm();
      if (isValid) {
         const processedData = {
            ...row,
            status: row.status ? 1 : 0,
         };

         try {
            if (isEditMode && currentRow?.id) {
               processedData.id = currentRow.id;
               await dispatch(updateCity(processedData));
            } else {
               await dispatch(addCity(processedData));
            }
            dispatch(getCities());
            onClose();
         } catch (error) {
            toast({
               title: 'Error',
               description: error.message || 'An error occurred while saving the city.',
               status: 'error',
               duration: 3000,
               isClosable: true,
            });
         }
      } else {
         toast({
            title: 'Validation Error',
            description: 'Please correct the errors before saving.',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
      }
   };

   const handlesavecontinue = async () => {
      const isValid = await validateForm();
      if (isValid) {
         const processedData = {
            ...row,
            status: row.status ? 1 : 0,
         };

         try {
            if (isEditMode && currentRow?.id) {
               processedData.id = currentRow.id;
               await dispatch(updateCity(processedData));
            } else {
               await dispatch(addCity(processedData));
            }
            dispatch(getCities());
            clearFormData();
         } catch (error) {
            toast({
               title: 'Error',
               description: error.message || 'An error occurred while saving the city.',
               status: 'error',
               duration: 3000,
               isClosable: true,
            });
         }
      } else {
         toast({
            title: 'Validation Error',
            description: 'Please correct the errors before saving.',
            status: 'error',
            duration: 3000,
            isClosable: true,
         });
      }
   };

   const handleCansel = async () => {
      onClose();
      if (!isEditMode) {
         clearFormData();
      }
   };

   return (
      <Modal isOpen={isOpen} onClose={handleCansel} size="4xl">
         <ModalOverlay />
         <ModalContent
            bg={useColorModeValue('white', '#2d3748')}
            color={useColorModeValue('gray.800', 'white')}
            borderRadius="20px"
            p={0}
            mx="15px"
            mt={{ base: '45%', md: '25%', xl: '15%' }}
            boxShadow="lg">
            <ModalHeader fontSize="20px" fontWeight="600" mb={{ base: 0, xl: 4 }}>
               {isEditMode ? 'Edit City' : 'Add New City'}
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
               <Grid templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(2, 1fr)', xl: 'repeat(2, 1fr)' }} gap={2}>
                  <GridItem className="seperator-width">
                     {DynamicInput(
                        'name',
                        'Name',
                        {
                           value: row.name,
                           onChange: (e) => handleInputChange('name', e.target.value),
                        },
                        errors,
                        textColor,
                        'text',
                        true,
                     )}
                  </GridItem>
                  <GridItem className="seperator-width">
                     {renderDynamicDropdown(
                        'stId',
                        'State',
                        stateList,
                        {
                           value: row.stId,
                           onChange: (value) => handleInputChange('stId', value),
                           showCustomNoOptions: true,
                           openfrom: 'state',
                        },
                        errors,
                        textColor,
                        true,
                        'Select State',
                     )}
                  </GridItem>
               </Grid>
            </ModalBody>
            <ModalFooter>
               {!isEditMode && (saveandcontinue === true) && (
                  <Button onClick={handlesavecontinue} colorScheme="green" ml={3}>
                     Save & continue
                  </Button>
               )}
               <Button onClick={handleSubmit} colorScheme="green" ml={3}>
                  {isEditMode ? 'Update' : 'Save & Finish'}
               </Button>
               <Button variant="ghost" onClick={handleCansel} ml={3}>
                  Cancel
               </Button>
            </ModalFooter>
         </ModalContent>
      </Modal>
   );
};

export default CityMasterForm;

// import React, { useState, useEffect } from 'react';
// import * as Yup from 'yup';
// import {
//   Grid,
//   GridItem,
//   useColorModeValue,
//   Modal,
//   ModalOverlay,
//   ModalContent,
//   ModalHeader,
//   ModalCloseButton,
//   ModalBody,
//   ModalFooter,
//   Button,
//   // Box,
//   // Stepper,
//   // Step,
//   // StepIndicator,
//   // StepStatus,
//   // StepIcon,
//   // StepNumber,
//   // StepTitle,
//   // StepDescription,
//   // StepSeparator,
//   useToast,
// } from '@chakra-ui/react';
// import { useDispatch, useSelector } from 'react-redux';
// import { renderFormControl } from 'utils/DynamicFormData';
// import {
//   getCities,
//   addCity,
//   updateCity,
// } from '../../../../../redux/actions/city';
// import { renderSelectFormControl } from 'utils/DynamicFormData';
// import { getCountries } from '../../../../../redux/actions/country';
// import { getStates } from '../../../../../redux/actions/state';

// // Yup Validation Schema
// const validationSchema = Yup.object().shape({
//   name: Yup.string()
//     .required('Name is required')
//     .min(3, 'Name must be at least 3 characters long'),
// });

// // const steps = [
// //   { title: 'First', description: 'Enter Color Information' },
// //   { title: 'Second', description: 'Review and Save' },
// // ];

// const CityMasterForm = ({
//   isOpen,
//   onClose,
//   isEditMode = false,
//   currentRow = null,
// }) => {
//   const [row, setRow] = useState({
//     name: '',
//     coid: '',
//     stId: '',
//     status: '',
//   });

//   const [errors, setErrors] = useState({});
//   // const [activeStep, setActiveStep] = useState(0);
//   const toast = useToast();
//   const textColor = useColorModeValue('gray.800', 'white');
//   const dispatch = useDispatch();
//   const countryList = useSelector((state) => state.country.list);
//   const stateList = useSelector((state) => state.state.list);

//   useEffect(() => {
//     dispatch(getCountries());
//     dispatch(getStates());
//   }, [dispatch]);

//   // Function to clear form data
//   const clearFormData = () => {
//     setRow({
//       name: '',
//       coid: '',
//       stId: '',
//       status: '',
//     });
//     setErrors({});
//   };

//   // Populate form with current row data when in edit mode
//   useEffect(() => {
//     if (!isEditMode) {
//       clearFormData(); // Clear data when adding a new item
//     } else if (isEditMode && currentRow) {
//       setRow({
//         name: currentRow.name || '',
//         coid: currentRow.coid || '',
//         stId: currentRow.stId || '',
//         status: currentRow.status || '',
//       });
//     }
//   }, [isEditMode, currentRow]);

//   const validateStep = async () => {
//     try {
//       // Validate the whole row object instead of individual fields
//       await validationSchema.validate(row, { abortEarly: false });

//       setErrors({}); // Clear previous errors
//       return true;
//     } catch (err) {
//       if (err instanceof Yup.ValidationError) {
//         const errorObj = {};
//         err.inner.forEach((error) => {
//           errorObj[error.path] = error.message;
//         });
//         setErrors(errorObj);
//         return false;
//       }
//       return false;
//     }
//   };

//   const handleInputChange = (field, value) => {
//     if (field === 'stId') {
//       // Find the state in the stateList
//       const selectedState = stateList.find(state => state.id === Number(value));

//       // If we found the state, update both stId and coid
//       if (selectedState) {
//         setRow(prev => ({
//           ...prev,
//           stId: value,
//           coid: selectedState.coid // Set the country ID based on the selected state
//         }));
//       } else {
//         setRow(prev => ({
//           ...prev,
//           stId: value
//         }));
//       }
//     } else {
//       setRow(prev => ({
//         ...prev,
//         [field]: value,
//       }));
//     }

//     setRow((prev) => ({
//       ...prev,
//       [field]: value,
//     }));

//     // Clear the specific field error when user starts typing
//     setErrors((prevErrors) => {
//       const newErrors = { ...prevErrors };
//       if (newErrors[field]) {
//         delete newErrors[field];
//       }
//       return newErrors;
//     });
//   };

//   // const handleNext = async () => {
//   //   const isValid = await validateStep();
//   //   if (isValid) {
//   //     if (activeStep < steps.length - 1) {
//   //       setActiveStep(activeStep + 1);
//   //     }
//   //   } else {
//   //     toast({
//   //       title: 'Validation Error',
//   //       description: 'Please correct the errors before proceeding.',
//   //       status: 'error',
//   //       duration: 3000,
//   //       isClosable: true,
//   //     });
//   //   }
//   // };

//   // const handlePrevious = () => {
//   //   if (activeStep > 0) {
//   //     setActiveStep(activeStep - 1);
//   //   }
//   // };

//   const onFormSubmit = async () => {
//     const processedData = {
//       ...row,
//       coid: Number(row.coid),
//       stId: Number(row.stId),
//       status: row.status ? 1 : 0,
//       // ordinal: Number(row.ordinal),
//       // primaryuqcid: Number(row.primaryuqcid),
//       // ratepersalesid: Number(row.ratepersalesid),
//       // rateperpurchaseid: Number(row.rateperpurchaseid)
//     };

//     try {
//       if (isEditMode && currentRow && currentRow.id) {
//         // Update existing item
//         processedData.id = currentRow.id;
//         await dispatch(updateCity(processedData));
//       } else {
//         // Add new item
//         await dispatch(addCity(processedData));
//       }
//       dispatch(getCities());
//       // Close the modal
//       onClose();
//     } catch (error) {
//       toast({
//         title: 'Error',
//         description:
//           error.message || 'An error occurred while saving the Country.',
//         status: 'error',
//         duration: 3000,
//         isClosable: true,
//       });
//     }
//   };

//   const handleSubmit = async () => {
//     const isValid = await validateStep();
//     if (isValid) {
//       onFormSubmit(); // Call the form submit logic if validation is successful
//     } else {
//       toast({
//         title: 'Validation Error',
//         description: 'Please correct the errors before saving.',
//         status: 'error',
//         duration: 3000,
//         isClosable: true,
//       });
//     }
//   };

//   return (
//     <Modal isOpen={isOpen} onClose={onClose} size="3xl">
//       <ModalOverlay />
//       <ModalContent
//         bg={useColorModeValue('white', '#2d3748')}
//         color={useColorModeValue('gray.800', 'white')}
//         borderRadius="20px"
//         p={5}
//         boxShadow="lg"
//       >
//         <ModalHeader fontSize="36px" fontWeight="600" mb={4}>
//           {isEditMode ? 'Edit City' : 'Add New City'}
//         </ModalHeader>
//         <ModalCloseButton />
//         <ModalBody>
//           {/* <Stepper index={activeStep} colorScheme="teal">
//             {steps.map((step, index) => (
//               <Step key={index}>
//                 <StepIndicator>
//                   <StepStatus
//                     complete={<StepIcon />}
//                     incomplete={<StepNumber />}
//                     active={<StepNumber />}
//                   />
//                 </StepIndicator>
//                 <Box flexShrink="0">
//                   <StepTitle>{step.title}</StepTitle>
//                   <StepDescription>{step.description}</StepDescription>
//                 </Box>
//                 <StepSeparator />
//               </Step>
//             ))}
//           </Stepper> */}

//           {/* {activeStep === 0 && ( */}
//           <Grid templateColumns="repeat(2, 1fr)" gap={6} mt={8}>
//             <GridItem>
//               {renderFormControl(
//                 'name',
//                 'Name',
//                 {
//                   value: row.name,
//                   onChange: (e) => handleInputChange('name', e.target.value),
//                 },
//                 errors,
//                 textColor,
//                 'text',
//                 true, // optional: mark as required
//               )}
//             </GridItem>

//             <GridItem w="100%">
//               {renderSelectFormControl(
//                 'stId',
//                 'State',
//                 stateList,
//                 {
//                   value: row.stId,
//                   onChange: (e) => handleInputChange('stId', e.target.value),
//                   // isDisabled: Boolean(row.coId && row.cityId), // Disable when auto-filled
//                   isDisabled: !!row.stateId, // Disable when a state is selected
//                 },
//                 errors,
//                 textColor,
//                 false, // isRequired
//                 'Select State', // placeholder
//               )}
//             </GridItem>

//             <GridItem w="100%">
//               {renderSelectFormControl(
//                 'coId',
//                 'Country',
//                 countryList,
//                 {
//                   value: row.coId,
//                   onChange: (e) => handleInputChange('coId', e.target.value),
//                   // isDisabled: Boolean(row.coId && row.cityId), // Disable when auto-filled
//                   isDisabled: true,
//                 },
//                 errors,
//                 textColor,
//                 false, // isRequired
//                 'Select Country', // placeholder
//               )}
//             </GridItem>
//           </Grid>
//           {/* )} */}

//           {/* {activeStep === 1 && (
//             <Box mt={8}>
//               <strong>Name:</strong> {row.name}
//               <br />
//               <strong>Ordinal:</strong> {row.ordinal}
//               <br />
//             </Box>
//           )} */}
//         </ModalBody>
//         <ModalFooter>
//           {/* {activeStep > 0 && (
//             <Button
//               onClick={handlePrevious}
//               variant="ghost"
//               colorScheme="teal"
//               mr={3}
//             >
//               Previous
//             </Button>
//           )}

//           {activeStep < steps.length - 1 && (
//             <Button onClick={handleNext} colorScheme="teal">
//               Next
//             </Button>
//           )} */}

//           <Button onClick={handleSubmit} colorScheme="green" ml={3}>
//             Save
//           </Button>

//           <Button variant="ghost" onClick={onClose} ml={3}>
//             Cancel
//           </Button>
//         </ModalFooter>
//       </ModalContent>
//     </Modal>
//   );
// };

// export default CityMasterForm;
